import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const badgeVariants = cva(
  'inline-flex items-center gap-1.5 font-medium whitespace-nowrap',
  {
    variants: {
      variant: {
        // Neutral
        default: 'bg-slate-100 text-slate-600',
        muted: 'bg-slate-50 text-slate-500',

        // Colors
        primary: 'bg-blue-100 text-blue-700',
        secondary: 'bg-purple-100 text-purple-700',
        success: 'bg-emerald-100 text-emerald-700',
        warning: 'bg-amber-100 text-amber-700',
        destructive: 'bg-red-100 text-red-700',
        indigo: 'bg-indigo-100 text-indigo-700',
        cyan: 'bg-cyan-100 text-cyan-700',
        pink: 'bg-pink-100 text-pink-700',

        // Outline
        outline: 'border border-slate-200 bg-white text-slate-600',

        // Impact/priority levels (from InsightCard)
        high: 'bg-red-100 text-red-700',
        medium: 'bg-amber-100 text-amber-700',
        low: 'bg-slate-100 text-slate-600',
        critical: 'bg-red-100 text-red-700',

        // Tier badges
        starter: 'bg-blue-100 text-blue-700',
        pro: 'bg-amber-100 text-amber-700',
        enterprise: 'bg-purple-100 text-purple-700',
        free: 'bg-slate-100 text-slate-600',

        // Status badges
        unsaved: 'bg-amber-100 text-amber-700',
        synced: 'bg-emerald-100 text-emerald-700',
        pending: 'bg-blue-100 text-blue-700',
        error: 'bg-red-100 text-red-700',
      },
      size: {
        xs: 'px-1.5 py-0.5 text-[10px] rounded',
        sm: 'px-2 py-0.5 text-xs rounded',
        md: 'px-2.5 py-1 text-xs rounded-full',
        lg: 'px-4 py-2 text-sm rounded-full',
      },
      interactive: {
        true: 'cursor-pointer transition-colors',
        false: '',
      },
    },
    compoundVariants: [
      // Interactive hover states
      {
        variant: 'default',
        interactive: true,
        className: 'hover:bg-slate-200',
      },
      {
        variant: 'primary',
        interactive: true,
        className: 'hover:bg-blue-200',
      },
      {
        variant: 'secondary',
        interactive: true,
        className: 'hover:bg-purple-200',
      },
      {
        variant: 'indigo',
        interactive: true,
        className: 'hover:bg-indigo-200',
      },
      // Tier badge interactive states
      {
        variant: 'free',
        interactive: true,
        className: 'hover:bg-slate-200',
      },
      {
        variant: 'starter',
        interactive: true,
        className: 'hover:bg-blue-200',
      },
      {
        variant: 'pro',
        interactive: true,
        className: 'hover:bg-amber-200',
      },
      {
        variant: 'enterprise',
        interactive: true,
        className: 'hover:bg-purple-200',
      },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'md',
      interactive: false,
    },
  }
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant, size, interactive, ...props }, ref) => {
    return (
      <span
        ref={ref}
        className={cn(badgeVariants({ variant, size, interactive, className }))}
        {...props}
      />
    );
  }
);
Badge.displayName = 'Badge';

export { Badge, badgeVariants };
